import { createReducer, on } from '@ngrx/store';
import * as fromPortfolioActions from './portfolio.actions';
import {
  CashAccountId,
  CurrentNumber,
  DepositaryMobileBank,
  FavoriteNumbers,
  LoadingStatus,
  Performance,
  PerformanceEnum,
  Performances,
  Portfolio,
  PortfolioNumber,
  Transaction
} from '@qvs/core';
import { clone, merge } from 'lodash-es';
import { AssetHelper } from '~/shared/helpers/asset-helper';
import { setCurrentClient } from '@qvs/core/user';

export const portfolioFeatureKey = 'portfolio';

export interface State {
  currentNumber: CurrentNumber;
  currentCashAccountIds: CashAccountId[];
  favoriteNumbers: FavoriteNumbers;
  portfolios: Portfolio[];
  creditCardPortfolioIds: string[];
  portfoliosLoadingStatus: LoadingStatus;
  portfolioLoadingStatus: LoadingStatus;
  performanceLoadingStatus: {
    currentYear: LoadingStatus;
    previousYear: LoadingStatus;
    inception: LoadingStatus;
  };
  currentInstrumentPositionId?: number;
  currentAssetAllocationId?: string;
  depositaryBanks?: DepositaryMobileBank[];
  portfolioPerformanceTimeRange?: PerformanceEnum;
}

export const initialState: State = {
  currentNumber: null,
  currentCashAccountIds: [],
  favoriteNumbers: [],
  portfolios: [],
  creditCardPortfolioIds: [],
  portfoliosLoadingStatus: LoadingStatus.IDLE,
  portfolioLoadingStatus: LoadingStatus.IDLE,
  performanceLoadingStatus: {
    currentYear: LoadingStatus.IDLE,
    previousYear: LoadingStatus.IDLE,
    inception: LoadingStatus.IDLE
  },
  portfolioPerformanceTimeRange: PerformanceEnum.CURRENT_YEAR
};

export const updatePortfolioCashAccountTransactions = (
  portfolioNumber: PortfolioNumber,
  cashAccountId: CashAccountId,
  portfolios: Portfolio[],
  isLoading: boolean,
  transactions?: Transaction[]
): Portfolio[] =>
  portfolios.map((portfolio) => {
    if (portfolio.portfolioNumber !== portfolioNumber) {
      return portfolio;
    }

    const cashAllocation = AssetHelper.getCashAllocation(portfolio.assetAllocations);
    if (!cashAllocation) {
      return portfolio;
    }

    const updatedAccounts = cashAllocation?.accounts?.map((account) => {
      if (account.id !== cashAccountId) {
        return account;
      }

      const existingTransactions = account.transactions || [];
      let newTransactions = [];
      if (transactions) {
        newTransactions = transactions.filter(
          (tx) =>
            tx.cashAccountId === cashAccountId &&
            !existingTransactions.some((existingTx) => JSON.stringify(existingTx) === JSON.stringify(tx))
        );
      }

      return {
        ...account,
        isLoading,
        transactions: [...existingTransactions, ...newTransactions]
      };
    });

    const updatedAllocations = portfolio.assetAllocations.map((allocation) =>
      allocation === cashAllocation ? { ...cashAllocation, accounts: updatedAccounts } : allocation
    );

    return { ...portfolio, assetAllocations: updatedAllocations };
  });

const updatePortfolioPerformance = (
  state: State,
  portfolioId: PortfolioNumber,
  key: keyof Performances,
  performance: Performance
) => {
  const portfolios = [...state.portfolios];
  const index = portfolios.findIndex((p) => p.portfolioNumber === portfolioId);

  if (portfolios[index]) {
    portfolios[index] = {
      ...portfolios[index],
      performance: {
        ...portfolios[index].performance,
        [key]: performance
      }
    };
  }

  return portfolios;
};

export const reducer = createReducer(
  initialState,
  on(fromPortfolioActions.toggleFavoritePortfolioSuccess, (state, { favoriteNumbers }) => ({
    ...state,
    favoriteNumbers
  })),
  on(fromPortfolioActions.setCurrentPortfolio, (state, { portfolio }) => ({
    ...state,
    currentNumber: portfolio ? portfolio.portfolioNumber : null,
    currentCashAccountIds: []
  })),
  on(fromPortfolioActions.setCreditCardPortfolioIds, (state, { creditCardPortfolioIds }) => ({
    ...state,
    creditCardPortfolioIds
  })),
  on(fromPortfolioActions.loadPortfolio, (state, { portfolioNumber }) => {
    const portfolios = [...state.portfolios];
    const existingPortfolioIndex = portfolios.findIndex((item) => item.portfolioNumber === portfolioNumber);
    if (existingPortfolioIndex >= 0) {
      portfolios[existingPortfolioIndex] = { ...portfolios[existingPortfolioIndex], loadingStatus: LoadingStatus.LOADING };
    } else {
      portfolios.push({ portfolioNumber, loadingStatus: LoadingStatus.LOADING } as Portfolio);
    }
    return {
      ...state,
      portfolios,
      portfolioLoadingStatus: LoadingStatus.LOADING
    };
  }),
  on(setCurrentClient, (state) => ({
    ...state,
    currentNumber: null
  })),
  on(fromPortfolioActions.loadPortfolios, (state) => ({
    ...state,
    portfoliosLoadingStatus: LoadingStatus.LOADING
  })),
  on(fromPortfolioActions.initialLoadPortfolios, (state) => ({
    ...initialState,
    favoriteNumbers: state.favoriteNumbers || [],
    portfoliosLoadingStatus: LoadingStatus.LOADING
  })),
  on(fromPortfolioActions.loadPortfoliosSuccess, (state, action) => ({
    ...state,
    portfolios: [...action.portfolios],
    portfoliosLoadingStatus: LoadingStatus.SUCCEEDED
  })),
  on(fromPortfolioActions.loadPortfoliosFailure, (state) => ({
    ...state,
    portfoliosLoadingStatus: LoadingStatus.FAILED
  })),
  on(fromPortfolioActions.loadPortfolio, (state) => ({
    ...state,
    portfolioLoadingStatus: LoadingStatus.LOADING
  })),
  on(fromPortfolioActions.loadPortfolioFailure, (state, { portfolioNumber }) => {
    const portfolios = [...state.portfolios];
    const existingPortfolioIndex = state.portfolios.findIndex((item) => item.portfolioNumber === portfolioNumber);
    if (existingPortfolioIndex >= 0) {
      portfolios[existingPortfolioIndex].loadingStatus = LoadingStatus.FAILED;
    } else {
      portfolios.push({ portfolioNumber, loadingStatus: LoadingStatus.FAILED });
    }
    return {
      ...state,
      portfolioLoadingStatus: LoadingStatus.FAILED,
      portfolios
    };
  }),
  on(fromPortfolioActions.loadPortfolioSuccess, (state, { portfolio }) => {
    const portfolios = [...state.portfolios];
    const existingPortfolioIndex = portfolios.findIndex((item) => item.portfolioNumber === portfolio.portfolioNumber);
    if (existingPortfolioIndex >= 0) {
      const existingPortfolio = clone(portfolios[existingPortfolioIndex]);
      existingPortfolio.loadingStatus = LoadingStatus.SUCCEEDED;
      portfolios[existingPortfolioIndex] = merge({}, existingPortfolio, portfolio);
    } else {
      portfolios.push(portfolio);
    }
    return {
      ...state,
      portfolioLoadingStatus: LoadingStatus.SUCCEEDED,
      portfolios
    };
  }),
  on(fromPortfolioActions.setCurrentInstrument, (state, action) => ({
    ...state,
    currentInstrumentPositionId: action.instrument.positionId
  })),
  on(fromPortfolioActions.setCurrentAssetAllocation, (state, action) => ({
    ...state,
    currentAssetAllocationId: action.assetAllocation.id
  })),
  on(fromPortfolioActions.setCurrentCashAccount, (state, { cashAccountId }) => {
    if (cashAccountId) {
      return {
        ...state,
        currentCashAccountIds: [cashAccountId]
      };
    }
    return {
      ...state,
      currentCashAccountIds: []
    };
  }),
  on(fromPortfolioActions.lazyLoadTransactionsForCashAccounts, (state, { portfolioNumber, cashAccountId }) => {
    const portfolios = [...state.portfolios];
    return {
      ...state,
      portfolios: updatePortfolioCashAccountTransactions(portfolioNumber, cashAccountId, portfolios, true)
    };
  }),
  on(fromPortfolioActions.loadTransactionsForCashAccountsSuccess, (state, { portfolioNumber, cashAccountId, transactions }) => {
    const portfolios = [...state.portfolios];
    return {
      ...state,
      portfolios: updatePortfolioCashAccountTransactions(portfolioNumber, cashAccountId, portfolios, false, transactions)
    };
  }),
  on(fromPortfolioActions.loadTransactionsForCashAccountsFailure, (state, { portfolioNumber, cashAccountId }) => {
    const portfolios = [...state.portfolios];
    return {
      ...state,
      portfolios: updatePortfolioCashAccountTransactions(portfolioNumber, cashAccountId, portfolios, false)
    };
  }),
  on(fromPortfolioActions.loadYtdPerformance, (state) => ({
    ...state,
    performanceLoadingStatus: { ...state.performanceLoadingStatus, currentYear: LoadingStatus.LOADING }
  })),
  on(fromPortfolioActions.loadYtdPerformanceSuccess, (state, { performance }) => ({
    ...state,
    portfolios: updatePortfolioPerformance(state, performance.portfolioId, 'currentYearPerformance', performance),
    performanceLoadingStatus: { ...state.performanceLoadingStatus, currentYear: LoadingStatus.SUCCEEDED }
  })),
  on(fromPortfolioActions.loadYtdPerformanceFailure, (state) => ({
    ...state,
    performanceLoadingStatus: { ...state.performanceLoadingStatus, currentYear: LoadingStatus.FAILED }
  })),
  on(fromPortfolioActions.loadPreviousYearPerformance, (state) => ({
    ...state,
    performanceLoadingStatus: { ...state.performanceLoadingStatus, previousYear: LoadingStatus.LOADING }
  })),
  on(fromPortfolioActions.loadPreviousYearPerformanceSuccess, (state, { performance }) => ({
    ...state,
    portfolios: updatePortfolioPerformance(state, performance.portfolioId, 'previousYearPerformance', performance),
    performanceLoadingStatus: { ...state.performanceLoadingStatus, previousYear: LoadingStatus.SUCCEEDED }
  })),
  on(fromPortfolioActions.loadPreviousYearPerformanceFailure, (state) => ({
    ...state,
    performanceLoadingStatus: { ...state.performanceLoadingStatus, previousYear: LoadingStatus.FAILED }
  })),
  on(fromPortfolioActions.loadInceptionPerformance, (state) => ({
    ...state,
    performanceLoadingStatus: { ...state.performanceLoadingStatus, inception: LoadingStatus.LOADING }
  })),
  on(fromPortfolioActions.loadInceptionPerformanceSuccess, (state, { performance }) => ({
    ...state,
    portfolios: updatePortfolioPerformance(state, performance.portfolioId, 'inceptionPerformance', performance),
    performanceLoadingStatus: { ...state.performanceLoadingStatus, inception: LoadingStatus.SUCCEEDED }
  })),
  on(fromPortfolioActions.loadInceptionPerformanceFailure, (state) => ({
    ...state,
    performanceLoadingStatus: { ...state.performanceLoadingStatus, inception: LoadingStatus.FAILED }
  })),
  on(fromPortfolioActions.loadDepositaryBanksSuccess, (state, action) => ({
    ...state,
    depositaryBanks: action.depositaryBanks
  })),
  on(fromPortfolioActions.setPortfolioPerformanceTimeRange, (state, action) => ({
    ...state,
    portfolioPerformanceTimeRange: action.portfolioPerformanceTimeRange
  }))
);
